import { get, post } from '../client'
import type { PkActivity, PkMetric, PkMode } from '@/pages/marketing-pk/types'

export interface MarketingPkFireworkEvent {
  id: number
  companyId: number
  activityId: number
  eventId: string
  eventType?: 'final_victory'
  team: string
  teamName: string
  stageTitle: string
  achievedValue: number
  claimToken: string
  noticeTitle: string
  noticeContent: string
  hasSound: boolean
  metricKey: PkMetric
  durationSeconds: number
  activityName?: string
}

export interface MarketingPkListReq {
  page?: number
  pageSize?: number
  status?: string
  mode?: string
  metricKey?: string
  keyword?: string
}

/** 后端实体字段，禁止将参赛名单当作聚合战队。 */
export interface MarketingPkActivityDto {
  canManage: boolean
  canSettle: boolean
  id: number
  activityNo: string
  title: string
  mode: PkMode
  metricKey: PkMetric
  projectId: number
  projectName?: string
  status: PkActivity['status']
  startTime: string
  endTime: string
  coolingPeriodDays: number
  sprintHideProgress: boolean
  participantsJson: string
  stagesJson: string
  snapshotsJson: string
  rewardsJson: string
  notificationJson: string
  fireworksJson: string
  winnerTeam: string
  submitterId?: number
  submitterName?: string
  submittedAt?: string
  creatorName: string
  createdAt: string
  settledAt?: string
  settledBy?: number
  settledByName?: string
  refundCutoff?: string
  winnerReward?: string
  loserPunishment?: string
  drawTitle?: string
  drawAmount?: number
}
export interface MarketingPkTeamStats {
  team: 'red' | 'blue'
  teamName: string
  currentValue: number
  memberCount: number
  isMasked: boolean
  resultValid: boolean
}
export interface MarketingPkMemberDto {
  employeeId: number
  employeeNo: string
  name: string
  team: 'red' | 'blue'
  teamName: string
  currentValue: number
  groupRank: number
  firstOrderCount: number
  repeatOrderCount: number
  productQuantity: number
  fanCount: number
  convertedFanCount: number
  validOrderCount: number
  paidAmount: number
  refundAmount: number
  netSales: number
  previousNetSales: number
  previousChangeRate: number
  isMasked: boolean
  resultValid: boolean
}
export interface MarketingPkTimelineEventDto {
  id: string
  time: string
  team?: 'red' | 'blue' | string
  teamName?: string
  type: 'order' | 'milestone' | 'sprint' | 'system' | string
  stage?: number
  content: string
  valueChange?: string
}
export interface MarketingPkTimelineResp {
  list: MarketingPkTimelineEventDto[]
  total: number
  page: number
  pageSize: number
}
export interface MarketingPkDetailResp {
  activity: MarketingPkActivityDto
  redTeamStats: MarketingPkTeamStats
  blueTeamStats: MarketingPkTeamStats
  serverNow: string
  coolingDeadline?: string
  sprintMaskActive: boolean
  canManage: boolean
  canSettle: boolean
  resultValid: boolean
  invalidReason: string
  statisticsStatus: 'ready' | 'error'
}
export interface MarketingPkSourceDto {
  sourceType: string
  orderId: number
  orderNo: string
  customerId: number
  employeeId: number
  firstAuditedAt: string
  amount: number
  refund: number
  quantity: number
  firstOrder: boolean
  valid: boolean
}
export interface MarketingPkDeliveryDto {
  id: number
  channel: string
  eventType: string
  team: string
  stage: number
  status: string
  attempts: number
  errorCode: string
  updatedAt: string
}

/** 保存或更新 PK 草稿的完整配置入参 */
export interface SaveMarketingPkDraftReq {
  id?: string | number
  createRequestKey?: string
  title: string
  mode: PkMode
  metricKey: PkMetric
  /** 所属项目ID；0或不传表示统计全部项目。 */
  projectId?: number
  startTime: string
  endTime: string
  coolingPeriodDays: number
  sprintHideProgress: boolean
  fireworkDuration?: number
  winnerReward?: string
  loserPunishment?: string
  drawTitle?: string
  drawAmount?: number
  participantsJson: string
  stagesJson: string
  rewardsJson: string
  notificationJson: string
  fireworksJson: string
}

/** 上一自然月双方业绩预览请求 */
export interface MarketingPkPreviousMonthPreviewReq {
  startTime: string
  metricKey: PkMetric
  participantsJson: string
  projectId?: number
}

/** 上一自然月双方业绩预览响应 */
export interface MarketingPkPreviousMonthPreviewResp {
  resultValid: boolean
  invalidReason: string
  hasData: boolean
  metricKey: PkMetric
  redValue: number
  blueValue: number
  periodStart: string
  periodEnd: string
}

/** 分页获取活动列表 */
export function getMarketingPkList(params: MarketingPkListReq) {
  return get<{ list: MarketingPkActivityDto[]; total: number }>('/business/marketingPk/getList', {
    params: params as Record<string, unknown>,
  })
}

/** 获取活动战况详情 */
export function getMarketingPkDetail(activityId: number | string) {
  return get<MarketingPkDetailResp>('/business/marketingPk/getDetail', { params: { activityId } })
}

/** 获取纯净人员统计 */
export function getMarketingPkMemberStats(activityId: number | string) {
  return get<MarketingPkMemberDto[]>('/business/marketingPk/getMemberStats', {
    params: { activityId },
  })
}

/** 获取当前访问者可见的 PK 战况；page/pageSize 用于最新战况和完整战况抽屉分页。 */
export function getMarketingPkTimeline(activityId: number | string, page = 1, pageSize = 5) {
  return get<MarketingPkTimelineResp>('/business/marketingPk/getTimeline', {
    params: { activityId, page, pageSize },
  })
}

/** 将界面活动 ID 转成后端 int64 所需的安全正整数；id 为路由或实体 ID。 */
function numericActivityId(id: string | number): number {
  const value = Number(id)
  if (!Number.isSafeInteger(value) || value <= 0) throw new Error('活动 ID 无效，请刷新后重试')
  return value
}

/** 保存或更新草稿 */
export function saveMarketingPkDraft(data: SaveMarketingPkDraftReq) {
  return post<{ id: number }>('/business/marketingPk/saveDraft', {
    ...data,
    id: data.id == null ? undefined : numericActivityId(data.id),
  })
}

/** 获取活动开始月份之前一个自然月的双方业绩 */
export function getMarketingPkPreviousMonthPreview(data: MarketingPkPreviousMonthPreviewReq) {
  return post<MarketingPkPreviousMonthPreviewResp>(
    '/business/marketingPk/getPreviousMonthPreview',
    data,
  )
}

/** 提交审批发布 */
export function submitMarketingPkApproval(activityId: number | string) {
  return post<null>('/business/marketingPk/submitApproval', { id: numericActivityId(activityId) })
}

/** 取消活动 (仅限待审批或待开始) */
export function cancelMarketingPkActivity(activityId: number | string, cancelReason?: string) {
  return post<null>('/business/marketingPk/cancelActivity', {
    id: numericActivityId(activityId),
    reason: cancelReason,
  })
}

/** 提前结束冷静期 */
export function earlyEndMarketingPkCooling(activityId: number | string) {
  return post<null>('/business/marketingPk/earlyEndCooling', { id: numericActivityId(activityId) })
}

/** 手动结算活动 */
export function settleMarketingPkActivity(activityId: number | string) {
  return post<null>('/business/marketingPk/settle', { id: numericActivityId(activityId) })
}

/** 获取当前员工 10 分钟 TTL 内最新待播礼花 1 条 */
export function getPendingMarketingPkFireworks() {
  return get<MarketingPkFireworkEvent | null>('/business/marketingPk/getPendingFireworks')
}

/** 确认消费礼花回执 ACK */
export function ackMarketingPkFirework(eventId: string, claimToken: string) {
  return post<null>('/business/marketingPk/ackFirework', { eventId, claimToken })
}

/** 获取已按身份脱敏的统计来源，activityId 为当前活动。 */
export function getMarketingPkSources(activityId: string | number) {
  return get<MarketingPkSourceDto[]>('/business/marketingPk/getSources', { params: { activityId } })
}
/** 获取真实通知投递记录，activityId 为当前活动。 */
export function getMarketingPkNotificationLogs(activityId: string | number) {
  return get<MarketingPkDeliveryDto[]>('/business/marketingPk/getNotificationLogs', {
    params: { activityId: numericActivityId(activityId) },
  })
}

/** 查询服务端校验过的礼花管理接收人；keyword 为姓名关键词。 */
export function getMarketingPkFireworkManagers(keyword = '') {
  return get<Array<{ value: number; label: string }>>('/business/marketingPk/getFireworkManagers', {
    params: { keyword },
  })
}
